import React, { useState, useEffect, useMemo } from "react";
import { Button, Icon, Table, Field, Groupper, Input, Message, Header, Dropdown } from 'react-frontier2';
import { bindClick, bindFormChange } from "@arema/components/Util";
import { Link } from "react-router-dom";
import { SearchUserModal } from "../../components";
import { ShortUser } from "@arema/components/Classes";
import { useTitle } from "@arema/components/Hooks";
import Validator, { FormErrors } from "@arema/components/Validator";
import API from "../../API";

enum ModalType {
	SEARCH_ZONE = 1,
	SEARCH_USER = 2,
}

var EventList = ()=>{
	var { setTitle } = useTitle();
	var [promoterList, setPromoterList] = useState<{
		promoter_id: number,
		promoter_name: string,
		user_assigned: number,
		user_assigned_name: string,
	}[]>(null);
	var [typesList, setTypesList] = useState<{
		type_id: number,
		description: string,
	}[]>(null);
	var [categoryList, setCategoryList] = useState<any[]>(null);
	var [shownModal, setShownModal] = useState<ModalType>(null);
	var [qlError, setQlError] = useState<string>(null);
	var [eventCreated, setEventCreated] = useState<{
		event_id: number,
		event_name: string,
		promoter_name: string,
		producer_name: string,
	}>(null);

	var [data, setData] = useState<{
		event_name: string,
		promoter_id: number,
		type_id: number,
		category_id: number,
		producer_name: string,
	}>({
		event_name: '',
		promoter_id: null,
		type_id: null,
		category_id: null,
		producer_name: ''
	});
	var [options, setOptions] = useState<{
		commission_return: string,
		user_assigned: ShortUser,
	}>({
		commission_return: '',
		user_assigned: null
	})
	var [errorPrompts, setErrorPrompts] = useState<string[]>(null);
	var [formErrors, setFormErrors] = useState<FormErrors>({});

	var selected_promoter = useMemo(()=>{
		if(!data.promoter_id || !promoterList) return null;
		return promoterList.find(a=>a.promoter_id===data.promoter_id);
	}, [data.promoter_id, promoterList]);

	useEffect(()=>{
		setTitle('Crear evento');
		if(!promoterList){
			API.getEventsQL(['promoter', 'types', 'categories']).then(res=>{
				if(res.error) return setQlError(res.message);
				setPromoterList(res.data.promoter);
				setCategoryList(res.data.categories);
				setTypesList(res.data.types);
			}).catch(err=>{
				setQlError('Hubo un error inesperado cargando la información complementaria (LCL-1)');
			})
		}
	}, []);

	var onUserAssigned = (user: ShortUser)=>{
		onOptionsChange('user_assigned')(user);
		setShownModal(null);
	}

	var submit = (setLoading: (b: boolean)=>void)=>{
		var commission_return = null;
		if(options.commission_return && options.commission_return.length>0){
			var cr = parseFloat(options.commission_return);
			if(!Number.isNaN(cr)){
				commission_return = cr/100;
			}
		}

		var { valid, errors, prompts, form } = Validator({ 
			event_name: data.event_name,
			promoter_id: data.promoter_id,
			event_type: data.type_id,
			category_id: data.category_id,
			commission_return: commission_return,
			producer_name: data.producer_name
		}, {
			event_name: [
				{ rule: 'maxLength', params: [64], prompt: 'El nombre del evento debe de ser de máximo 64 caracteres.' },
				{ rule: 'minLength', params: [5], prompt: 'El nombre del evento debe de ser de minimo 5 caracteres.' },
			],
			promoter_id: [
				{ rule: 'empty', prompt: 'Favor de seleccionar el promotor del evento.' },
			],
			event_type: [
				{ rule: 'empty', prompt: 'Favor de seleccionar el tipo de evento.' },
			],
			category_id: [
				{ rule: 'empty', prompt: 'Favor de seleccionar la categoría del evento.' },
			],
			commission_return: [
				{ rule: 'min', params: [0], prompt: 'El retorno debe de ser un número positivo', skipEmpty: true },
				{ rule: 'max', params: [1], prompt: 'El retorno debe de ser entre 0 y 100', skipEmpty: true }
			],
			producer_name: [
				{ rule: 'minLength', params: [4], skipEmpty: true }
			]
		});
		setFormErrors(errors);
		setErrorPrompts(prompts);
		if(!valid) return;
		setLoading(true);
		API.createEvent(form.event_name, form.promoter_id, form.event_type, form.category_id, {
			commission_return,
			user_assigned: options.user_assigned?.user_id,
			producer_name: form.producer_name,
		}).then(res=>{
			if(res.error) return setErrorPrompts([res.message]);
			var promoter = promoterList.find(a=>a.promoter_id===form.promoter_id);
			setEventCreated({
				event_id: res.data.event_id,
				event_name: form.event_name,
				promoter_name: promoter.promoter_name,
				producer_name: form.producer_name,
			})
			// navigate(`/events/${res.data.event_id}`);
		}).catch(err=>{
			return setErrorPrompts(['Hubo un error inesperado creando el evento (LCL-1)']);
		}).finally(() => {
			setLoading(false);
		})
	}

	var onPromoterChange = (promoter_id: number)=>{
		var pr = promoterList.find(a=>a.promoter_id===promoter_id);
		if(!pr) return;
		setData({
			...data,
			promoter_id: pr.promoter_id,
		});
		if(pr.user_assigned){
			setOptions({
				...options,
				user_assigned: {
					first_name: '',
					last_name: '',
					name: pr.user_assigned_name,
					email: null,
					superadmin: null,
					user_id: pr.user_assigned,
					user_type: null,
				}
			})
		}
	}

	var onDataChange = bindFormChange(data, setData);
	var onOptionsChange = bindFormChange(options, setOptions);

	if(eventCreated){
		return <Groupper title={'Evento creado'} titleCentered style={{ maxWidth: 500, margin: 'auto' }}>
			<Icon name='check-circle' style={{ textAlign: 'center', display: 'block', fontSize: 80, width: '100%' }} />
			<Header text={eventCreated.event_name} style={{ fontSize: 20, marginTop: 15 }} subtext={eventCreated.promoter_name} subtextStyle={{ fontSize: 14 }} />
			<Table details striped className='top bottom' style={{ borderBottomLeftRadius: 12, borderBottomRightRadius: 12, borderBottom: '1px solid #e0e0e0' }}>
				<Table.Row data={['ID', eventCreated.event_id]} />
				<Table.Row data={['Nombre', eventCreated.event_name]} />
				<Table.Row data={['Promotor', eventCreated.promoter_name]} />
				{!!eventCreated?.producer_name && eventCreated?.producer_name.length>=4 && (
					<Table.Row data={['Productor', eventCreated.producer_name]} />
				)}
			</Table>

			<div className="actions" style={{ textAlign: 'center' }}>
				<Button text='Ver evento' color='black' size='big' as={Link} to={`/events/${eventCreated.event_id}`} />
			</div>
		</Groupper>
	}

	return <>
		<Groupper title="Crear Evento" style={{ maxWidth: 500, margin: 'auto' }} actions={qlError ? null : (
			<Button text="Crear evento" color='black' onClick={submit} />
		)}>
			{qlError ? (
				<Header text="Error" subtext={qlError} />
			) : (
				<>
					<Input value={data.event_name} onChange={onDataChange('event_name')} label="Nombre" placeholder="Nombre del evento" error={formErrors['event_name']} />
					<Dropdown search 
						loading={!!!promoterList}
						label="Promotor" 
						value={data.promoter_id} 
						onValueChange={onPromoterChange} 
						type={formErrors['promoter_id'] ? 'error' : null}
						items={promoterList ? promoterList.map(a=>({
							key: a.promoter_id,
							value: a.promoter_id,
							text: a.promoter_name
						})) : []} 
					/>
					<Field amount={2}>
						<Dropdown 
							loading={!!!typesList} 
							label="Tipo evento" 
							value={data.type_id} 
							type={formErrors['event_type'] ? 'error' : null}
							onValueChange={onDataChange('type_id')} 
							items={typesList ? typesList.map(a=>({
								value: a.type_id,
								key: a.type_id,
								text: a.description
							})) : []} 
						/>
						<Dropdown 
							loading={!!!categoryList} 
							label="Categoría" 
							value={data.category_id} 
							onValueChange={onDataChange('category_id')} 
							type={formErrors['category_id'] ? 'error' : null}
							items={categoryList ? categoryList.map(a=>({
								value: a.category_id,
								key: a.category_id,
								text: a.category_name,
							})) : []} 
						/>
					</Field>
					<Groupper.Divider style={{ color: 'gray', fontWeight: 'bold', textTransform: 'uppercase', flexDirection: 'column', alignItems: 'flex-start', height: 'auto', marginBottom: 15 }}>
						Opciones <br />
						<div className="subtext" style={{ fontSize: 12, fontWeight: 'normal', marginTop: -5, color: 'gray', textTransform: 'none' }}>Configuración opcional del evento</div>
					</Groupper.Divider>
						<Input label="Productor" comment="Nombre del productor que se imprimirá en el boleto" placeholder={selected_promoter ? selected_promoter.promoter_name : null} value={data.producer_name} onChange={onDataChange('producer_name')} />
					<Input
						label="Retorno"
						comment="Retorno del promotor del evento"
						value={options.commission_return}
						onChange={onOptionsChange('commission_return')}
					/>
					<Field label="Responsable del evento" comment="Usuario responsable del evento">
						<Input readonly value={options.user_assigned?.name ||''} onClick={bindClick(setShownModal, ModalType.SEARCH_USER)} placeholder="Responsable del evento" buttons={(
							<Button iconName="pen" onClick={bindClick(setShownModal, ModalType.SEARCH_USER)} icon />
						)}/>
					</Field>
					<Message list={errorPrompts} type="error" />
				</>
			)}
		</Groupper>
		<SearchUserModal
			open={shownModal===ModalType.SEARCH_USER}
			onClose={bindClick(setShownModal)}
			onSelected={onUserAssigned}
		/>
	</>
}

export default EventList;